export type DunningStage = 'reminder' | '1' | '2' | '3';

export const DEFAULT_DUNNING_DEADLINE_DAYS = 7;
export const DUNNING_DEADLINE_MIN_DAYS = 1;
export const DUNNING_DEADLINE_MAX_DAYS = 30;

export const DUNNING_DOCUMENT_TEXT_TYPES = [
  'Erinnerung',
  '1. Mahnung',
  '2. Mahnung',
  '3. Mahnung',
] as const;

export type DunningDocumentTextType = (typeof DUNNING_DOCUMENT_TEXT_TYPES)[number];

export const DUNNING_STAGE_DOCUMENT_TYPE: Record<DunningStage, DunningDocumentTextType> = {
  reminder: 'Erinnerung',
  '1': '1. Mahnung',
  '2': '2. Mahnung',
  '3': '3. Mahnung',
};

export type DunningPlaceholderValues = {
  invoiceNumber: string;
  amount: string;
  dueDate: string;
  deadline: string;
  totalAmount: string;
  phone: string;
  email: string;
};

export const DUNNING_DEFAULT_TEXTS: Record<
  DunningDocumentTextType,
  { introLetterParas: string[]; closingBlocks: string[] }
> = {
  Erinnerung: {
    introLetterParas: [
      'Sehr geehrte Damen und Herren,',
      'sicherlich ist es im täglichen Trubel untergegangen. Für die nachfolgend aufgeführte Rechnung liegt uns bislang kein Zahlungseingang vor. Wir bitten Sie um kurzfristigen Ausgleich:',
    ],
    closingBlocks: [
      'Wir bitten Sie, den Gesamtbetrag in Höhe von {{TOTAL_AMOUNT}} bis spätestens {{DEADLINE}} auf unten stehendes Konto zu überweisen. Sollte die Zahlung zwischenzeitlich bereits erfolgt sein, betrachten Sie dieses Schreiben bitte als gegenstandslos.',
      'Für Rückfragen stehen wir Ihnen gerne zur Verfügung.',
    ],
  },
  '1. Mahnung': {
    introLetterParas: [
      'Sehr geehrte Damen und Herren,',
      'für die nachfolgend aufgeführte Rechnung liegt uns bislang kein Zahlungseingang vor. Wir gehen davon aus, dass dies versehentlich übersehen wurde, und bitten Sie um kurzfristigen Ausgleich:',
    ],
    closingBlocks: [
      'Wir bitten Sie, den Gesamtbetrag in Höhe von {{TOTAL_AMOUNT}} bis spätestens {{DEADLINE}} auf unten stehendes Konto zu überweisen. Sollte die Zahlung zwischenzeitlich bereits erfolgt sein, betrachten Sie dieses Schreiben bitte als gegenstandslos.',
      'Für Rückfragen stehen wir Ihnen gerne zur Verfügung.',
    ],
  },
  '2. Mahnung': {
    introLetterParas: [
      'Sehr geehrte Damen und Herren,',
      'unsere vorherige Mahnung blieb leider ohne Reaktion. Die nachfolgend aufgeführte Rechnung ist weiterhin offen. Wir fordern Sie auf, den Betrag kurzfristig auszugleichen:',
    ],
    closingBlocks: [
      'Wir fordern Sie auf, den Gesamtbetrag in Höhe von {{TOTAL_AMOUNT}} bis spätestens {{DEADLINE}} zu begleichen. Sollte keine Zahlung eingehen, sehen wir uns gezwungen, weitere Schritte einzuleiten.',
      'Für Rückfragen stehen wir Ihnen gerne zur Verfügung.',
    ],
  },
  '3. Mahnung': {
    introLetterParas: [
      'Sehr geehrte Damen und Herren,',
      'trotz mehrfacher Mahnungen ist die Zahlung der nachfolgend aufgeführten Rechnung bis heute ausgeblieben. Dies ist unsere letzte Zahlungsaufforderung:',
    ],
    closingBlocks: [
      'Wenn wir den Gesamtbetrag in Höhe von {{TOTAL_AMOUNT}} nicht bis spätestens {{DEADLINE}} verbuchen, werden wir den Vorgang ohne weitere Ankündigung an ein Inkassobüro / unsere Rechtsabteilung übergeben.',
      'Für Rückfragen stehen wir Ihnen gerne zur Verfügung.',
    ],
  },
};

export function isDunningDocumentTextType(value: string): value is DunningDocumentTextType {
  return (DUNNING_DOCUMENT_TEXT_TYPES as readonly string[]).includes(value);
}

export function dunningStageToDocumentType(stage: DunningStage): DunningDocumentTextType {
  return DUNNING_STAGE_DOCUMENT_TYPE[stage];
}

export function normalizeDunningDeadlineDays(raw: unknown): number {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(n)) return DEFAULT_DUNNING_DEADLINE_DAYS;
  return Math.min(DUNNING_DEADLINE_MAX_DAYS, Math.max(DUNNING_DEADLINE_MIN_DAYS, Math.floor(n)));
}

export function formatDunningDeadlineDate(deadlineDays: number, from = new Date()): string {
  const days = normalizeDunningDeadlineDays(deadlineDays);
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('de-DE');
}

export function formatDunningMoney(value: number | string | null | undefined, currency = 'EUR'): string {
  const n = Number(value ?? 0);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(safe);
}

export function formatDunningGermanDate(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (!raw) return '—';
  const isoDay = raw.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoDay)) {
    const [y, m, d] = isoDay.split('-').map((part) => Number(part));
    const date = new Date(y, m - 1, d);
    if (!Number.isNaN(date.getTime())) return date.toLocaleDateString('de-DE');
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleDateString('de-DE');
}

export function replaceDunningPlaceholders(text: string, values: DunningPlaceholderValues): string {
  return text
    .replaceAll('{{INVOICE_NUMBER}}', values.invoiceNumber)
    .replaceAll('{{AMOUNT}}', values.amount)
    .replaceAll('{{DUE_DATE}}', values.dueDate)
    .replaceAll('{{DEADLINE}}', values.deadline)
    .replaceAll('{{TOTAL_AMOUNT}}', values.totalAmount)
    .replaceAll('{{PHONE}}', values.phone)
    .replaceAll('{{EMAIL}}', values.email);
}

export function capitalizeDunningSalutation(text: string): string {
  return text.replace(/^sehr(\s+geehrte)/i, 'Sehr$1');
}

export function dunningDocumentTitle(stage: DunningStage, invoiceNumber: string | null): string {
  const ref = (invoiceNumber ?? '').trim() || '—';
  const type = dunningStageToDocumentType(stage);
  if (stage === 'reminder') return `Zahlungserinnerung zu Rechnung ${ref}`;
  if (stage === '1') return `1. Mahnung – Zahlungserinnerung zu Rechnung ${ref}`;
  if (stage === '3') return `Letzte Mahnung – Rechnung ${ref}`;
  return `${type} – Rechnung ${ref}`;
}

export const DUNNING_STAGE_LEAD_DOCUMENT_COLUMN: Record<
  DunningStage,
  'reminder_lead_document_id' | 'dunning_1_lead_document_id' | 'dunning_2_lead_document_id' | 'dunning_3_lead_document_id'
> = {
  reminder: 'reminder_lead_document_id',
  '1': 'dunning_1_lead_document_id',
  '2': 'dunning_2_lead_document_id',
  '3': 'dunning_3_lead_document_id',
};

export function dunningLeadDocumentId(
  row: Partial<Record<(typeof DUNNING_STAGE_LEAD_DOCUMENT_COLUMN)[DunningStage], string | null | undefined>>,
  stage: DunningStage,
): string | null {
  const id = String(row[DUNNING_STAGE_LEAD_DOCUMENT_COLUMN[stage]] ?? '').trim();
  return id || null;
}

export function dunningPdfFilename(stage: DunningStage, invoiceNumber: string | null): string {
  const ref = ((invoiceNumber ?? '').trim() || 'rechnung').replace(/[^\w.-]+/g, '_');
  const prefix =
    stage === 'reminder' ? 'Erinnerung' : stage === '1' ? '1-Mahnung' : stage === '2' ? '2-Mahnung' : '3-Mahnung';
  return `${prefix}_${ref}.pdf`;
}

export type DunningOpenInvoiceMatchInput = {
  id: string;
  organization_id?: string | null;
  party_name?: string | null;
  lead_id?: string | null;
};

export function isSameDunningCustomer(
  current: DunningOpenInvoiceMatchInput,
  other: DunningOpenInvoiceMatchInput,
): boolean {
  const orgId = String(current.organization_id ?? '').trim();
  const otherOrg = String(other.organization_id ?? '').trim();
  if (orgId && otherOrg) return orgId === otherOrg;
  if (orgId || otherOrg) return false;
  const party = String(current.party_name ?? '').trim().toLowerCase();
  const otherParty = String(other.party_name ?? '').trim().toLowerCase();
  if (party && otherParty) return party === otherParty;
  const leadId = String(current.lead_id ?? '').trim();
  const otherLead = String(other.lead_id ?? '').trim();
  return Boolean(leadId && otherLead && leadId === otherLead);
}
